import { useCallback, useMemo, useReducer, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { localeCode, useLocale, useT } from '@/shared/i18n'
import {
  createWeekConverterState,
  deriveDateToIso,
  deriveIsoToRange,
  formatIsoDate,
  formatIsoWeekday,
  weekConverterReducer,
  weekInputDate,
  type IsoDateResult,
  type IsoRangeResult,
} from '@/pages/week-number/model/weekNumber'

const UTILITY = 'week-number'

export function WeekNumberPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeName = localeCode(locale)
  const [state, dispatch] = useReducer(
    weekConverterReducer,
    undefined,
    createWeekConverterState,
  )
  const [notice, setNotice] = useState<string | null>(null)
  const {
    dateValue,
    isoYear,
    isoWeek,
    dateBlurred,
    isoYearBlurred,
    isoWeekBlurred,
  } = state

  const dateDerivation = useMemo(
    () => deriveDateToIso(dateValue, dateBlurred),
    [dateBlurred, dateValue],
  )
  const rangeDerivation = useMemo(
    () => deriveIsoToRange(
      isoYear,
      isoWeek,
      { isoYearBlurred, isoWeekBlurred },
    ),
    [isoWeek, isoWeekBlurred, isoYear, isoYearBlurred],
  )
  const dateResult = dateDerivation.result
  const rangeResult = rangeDerivation.result

  const handleDateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'change-date', value: event.target.value })
    setNotice(null)
  }, [])

  const handleIsoYearChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'change-iso-year', value: event.target.value })
    setNotice(null)
  }, [])

  const handleIsoWeekChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'change-iso-week', value: event.target.value })
    setNotice(null)
  }, [])

  const handleCopyDateToIso = useCallback(() => {
    if (!dateResult) return
    const startedAt = performance.now()
    toolInvoke(UTILITY, { action: 'copy_date_to_iso' })
    dispatch({ type: 'copy-date-to-iso', result: dateResult })
    setNotice(t.weekNumber.dateCopied)
    toolSuccess(
      UTILITY,
      Math.round(performance.now() - startedAt),
      { action: 'copy_date_to_iso' },
    )
  }, [dateResult, t.weekNumber.dateCopied])

  const handleCopyIsoToDate = useCallback(() => {
    if (!rangeResult) return
    const startedAt = performance.now()
    toolInvoke(UTILITY, { action: 'copy_iso_to_date' })
    dispatch({ type: 'copy-iso-to-date', result: rangeResult })
    setNotice(t.weekNumber.rangeCopied)
    toolSuccess(
      UTILITY,
      Math.round(performance.now() - startedAt),
      { action: 'copy_iso_to_date' },
    )
  }, [rangeResult, t.weekNumber.rangeCopied])

  const handleToday = useCallback(() => {
    dispatch({ type: 'use-today', value: weekInputDate(new Date()) })
    setNotice(null)
    toolInvoke(UTILITY, { action: 'today' })
  }, [])

  return (
    <div className="mx-auto max-w-5xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
          {t.weekNumber.title}
        </h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.weekNumber.description}
        </p>
      </section>

      <Card className="overflow-hidden">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-emerald-400"
        />
        <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.95fr)]">
          <div className="space-y-5 border-b p-5 sm:p-6 lg:border-b-0 lg:border-r">
            <section className="space-y-4 rounded-lg border bg-background/70 p-4 shadow-inner">
              <div>
                <h2 className="text-sm font-semibold tracking-normal">
                  {t.weekNumber.dateDirectionTitle}
                </h2>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  {t.weekNumber.reactiveHint}
                </p>
              </div>
              <label htmlFor="week-number-date" className="block">
                <span className="mb-2 block text-sm font-medium">
                  {t.weekNumber.dateLabel}
                </span>
                <input
                  id="week-number-date"
                  type="date"
                  value={dateValue}
                  onChange={handleDateChange}
                  onBlur={() => dispatch({ type: 'blur-date' })}
                  aria-invalid={dateDerivation.error !== null}
                  className="h-10 w-full rounded-md border border-input bg-card px-3 pr-10 text-sm shadow-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </label>
              {dateDerivation.error && (
                <p role="alert" className="text-sm text-destructive">
                  {t.weekNumber.invalidDate}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={handleCopyDateToIso}
                  disabled={!dateResult}
                >
                  {t.weekNumber.copyDateToIso}
                </Button>
                <Button type="button" variant="ghost" onClick={handleToday}>
                  {t.weekNumber.today}
                </Button>
              </div>
            </section>

            <section className="space-y-4 rounded-lg border bg-background/70 p-4 shadow-inner">
              <div>
                <h2 className="text-sm font-semibold tracking-normal">
                  {t.weekNumber.rangeDirectionTitle}
                </h2>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  {t.weekNumber.reactiveHint}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
                <label htmlFor="week-number-year" className="block">
                  <span className="mb-2 block text-sm font-medium">
                    {t.weekNumber.isoYearLabel}
                  </span>
                  <input
                    id="week-number-year"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={9999}
                    value={isoYear}
                    onChange={handleIsoYearChange}
                    onBlur={() => dispatch({ type: 'blur-iso-year' })}
                    aria-invalid={rangeDerivation.error !== null}
                    className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </label>
                <label htmlFor="week-number-week" className="block">
                  <span className="mb-2 block text-sm font-medium">
                    {t.weekNumber.isoWeekLabel}
                  </span>
                  <input
                    id="week-number-week"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={53}
                    value={isoWeek}
                    onChange={handleIsoWeekChange}
                    onBlur={() => dispatch({ type: 'blur-iso-week' })}
                    aria-invalid={rangeDerivation.error !== null}
                    className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </label>
              </div>
              {rangeDerivation.error && (
                <p role="alert" className="text-sm text-destructive">
                  {t.weekNumber.invalidIsoWeek}
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleCopyIsoToDate}
                disabled={!rangeResult}
              >
                {t.weekNumber.copyIsoToDate}
              </Button>
            </section>

            {notice && (
              <div
                role="status"
                className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground"
              >
                {notice}
              </div>
            )}

            <div className="rounded-lg border bg-secondary/30 p-4">
              <h2 className="text-sm font-semibold tracking-normal">
                {t.weekNumber.newYearTitle}
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t.weekNumber.newYearDescription}
              </p>
            </div>
          </div>

          <ResultPanel
            dateResult={dateResult}
            rangeResult={rangeResult}
            locale={localeName}
          />
        </CardContent>
      </Card>

      <section className="mt-10 grid gap-8 border-t pt-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.7fr)]">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.weekNumber.faqTitle}</h2>
          <dl className="mt-4 space-y-4">
            {t.weekNumber.faq.map((item) => (
              <div key={item.question}>
                <dt className="font-medium">{item.question}</dt>
                <dd className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</dd>
              </div>
            ))}
          </dl>
        </div>

        <aside>
          <h2 className="text-xl font-semibold tracking-normal">{t.weekNumber.relatedTitle}</h2>
          <div className="mt-4 space-y-2">
            {t.weekNumber.related.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="block rounded-md border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent"
              >
                <span className="text-sm font-medium">{item.title}</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                  {item.description}
                </span>
              </a>
            ))}
          </div>
        </aside>
      </section>
    </div>
  )
}

function ResultPanel({
  dateResult,
  rangeResult,
  locale,
}: {
  dateResult: IsoDateResult | null
  rangeResult: IsoRangeResult | null
  locale: string
}) {
  const t = useT()

  return (
    <div className="space-y-5 bg-secondary/30 p-5 sm:p-6">
      <div>
        <p className="text-sm font-semibold text-primary">{t.weekNumber.resultTitle}</p>
        {dateResult ? (
          <>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <span className="text-5xl font-bold leading-none tabular-nums tracking-normal sm:text-6xl">
                {dateResult.week}
              </span>
              <span className="pb-1 text-base font-medium text-muted-foreground">
                {t.weekNumber.weekUnit}
              </span>
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
              <Metric label={t.weekNumber.isoYear} value={dateResult.isoYear} accent />
              <Metric
                label={t.weekNumber.weekday}
                value={formatIsoWeekday(dateResult.date, locale)}
              />
              <Metric label={t.weekNumber.isoWeekday} value={dateResult.weekday} />
            </div>
          </>
        ) : (
          <div className="mt-4 rounded-lg border bg-card/80 p-4 shadow-sm">
            <p className="text-lg font-semibold tracking-normal">{t.weekNumber.emptyDateTitle}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {t.weekNumber.emptyDateDescription}
            </p>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card/80 p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold tracking-normal">{t.weekNumber.rangeTitle}</h2>
          {rangeResult && (
            <span className="rounded-md border bg-background px-2 py-1 font-mono text-xs text-muted-foreground">
              {rangeResult.isoYear}-W{String(rangeResult.week).padStart(2, '0')}
            </span>
          )}
        </div>
        {rangeResult ? (
          <>
            <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <CalendarTile label={t.weekNumber.weekStart} date={rangeResult.start} locale={locale} />
              <div className="flex min-w-12 items-center" aria-hidden="true">
                <span className="h-px flex-1 bg-border" />
                <span className="mx-1 h-2 w-2 rounded-full bg-primary" />
                <span className="h-px flex-1 bg-border" />
              </div>
              <CalendarTile label={t.weekNumber.weekEnd} date={rangeResult.end} locale={locale} />
            </div>
            <p className="mt-4 text-sm leading-6 text-muted-foreground">
              {formatIsoDate(rangeResult.start, locale)} - {formatIsoDate(rangeResult.end, locale)}
            </p>
          </>
        ) : (
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            {t.weekNumber.emptyRangeDescription}
          </p>
        )}
      </div>

      <div className="grid grid-cols-7 gap-1" aria-hidden="true">
        {Array.from({ length: 7 }, (_, index) => (
          <span
            key={index}
            className={cn(
              'h-3 rounded-full',
              dateResult && index + 1 === dateResult.weekday
                ? 'bg-primary'
                : 'bg-primary/20',
            )}
          />
        ))}
      </div>
    </div>
  )
}

function CalendarTile({
  label,
  date,
  locale,
}: {
  label: string
  date: Date
  locale: string
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-background text-center">
      <div className="bg-primary px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-normal text-primary-foreground">
        {label}
      </div>
      <div className="px-3 py-2">
        <div className="text-2xl font-bold leading-none tabular-nums">
          {String(date.getUTCDate()).padStart(2, '0')}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(date)}
        </div>
      </div>
    </div>
  )
}

function Metric({
  label,
  value,
  accent = false,
}: {
  label: string
  value: string | number
  accent?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2.5',
        accent ? 'bg-accent text-accent-foreground' : 'bg-background/70',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-normal">{value}</p>
    </div>
  )
}
